import React, {
  useState,
  useCallback,
  cloneElement,
  Children,
  useRef,
  useEffect,
} from "react";
/**
 * Drop-in replacement for react-step-wizard.
 *
 * Supports the subset of the API used in this codebase:
 *   - onStepChange({ activeStep })  — called after each step change (1-indexed)
 *   - isLazyMount                   — only render a step once it has been activated
 *   - instance(api)                 — called on mount with { goToNamedStep, goToStep, nextStep, previousStep }
 *   - showNavigation                — ignored (we never show built-in nav)
 *   - transitions                   — ignored (animations are disabled project-wide)
 *   - props                         — extra props forwarded to every step child
 *
 * Each step child receives:
 *   currentStep, totalSteps, isActive,
 *   nextStep(), previousStep(), goToStep(n), goToNamedStep(name)
 *
 * Step children declare their name via the `stepName` prop on the JSX element.
 */
interface StepWizardApi {
  goToNamedStep: (name: string) => void;
  goToStep: (n: number) => void;
  nextStep: () => void;
  previousStep: () => void;
}

interface StepWizardProps {
  children?: React.ReactNode;
  onStepChange?: (info: { activeStep: number; previousStep: number }) => void;
  isLazyMount?: boolean;
  instance?: (api: StepWizardApi) => void;
  showNavigation?: boolean;
  transitions?: Record<string, unknown>;
  props?: Record<string, unknown>;
}

const StepWizard = ({
  children,
  onStepChange,
  isLazyMount = false,
  instance,
  props: extraProps = {},
}: StepWizardProps) => {
  const [currentStep, setCurrentStep] = useState(1);

  const steps = Children.toArray(children).filter(
    Boolean
  ) as React.ReactElement<Record<string, unknown> & { stepName?: string }>[];
  const totalSteps = steps.length;

  // Keep mutable refs so callbacks never capture stale values.
  const currentStepRef = useRef(1);
  const onStepChangeRef = useRef(onStepChange);
  onStepChangeRef.current = onStepChange;

  // Build name → 1-based-index map from children's stepName props.
  // Rebuilt every render (children are stable in practice).
  const nameToIndex: Record<string, number> = {};
  steps.forEach((step, idx) => {
    if (step.props && step.props.stepName) {
      nameToIndex[step.props.stepName] = idx + 1;
    }
  });
  const nameToIndexRef = useRef(nameToIndex);
  nameToIndexRef.current = nameToIndex;

  const goToStep = useCallback(
    (n: number) => {
      const target = Math.min(Math.max(n, 1), totalSteps);
      setCurrentStep((prev) => {
        if (prev !== target && onStepChangeRef.current) {
          onStepChangeRef.current({ activeStep: target, previousStep: prev });
        }
        currentStepRef.current = target;
        return target;
      });
    },
    [totalSteps]
  );

  const goToNamedStep = useCallback(
    (name: string) => {
      const idx = nameToIndexRef.current[name];
      if (idx !== undefined) goToStep(idx);
    },
    [goToStep]
  );

  const nextStep = useCallback(() => {
    goToStep(currentStepRef.current + 1);
  }, [goToStep]);

  const previousStep = useCallback(() => {
    goToStep(currentStepRef.current - 1);
  }, [goToStep]);

  // Expose the wizard API to the parent via the `instance` callback.
  useEffect(() => {
    if (instance) {
      instance({ goToNamedStep, goToStep, nextStep, previousStep });
    }
  }, []);

  // Track which steps have been activated at least once (for isLazyMount).
  const renderedStepsRef = useRef(new Set([1]));

  return (
    <>
      {steps.map((step, idx) => {
        const stepNumber = idx + 1;
        const isActive = stepNumber === currentStep;

        if (isActive) {
          renderedStepsRef.current.add(stepNumber);
        }

        if (isLazyMount && !renderedStepsRef.current.has(stepNumber)) {
          return null;
        }

        return (
          <div
            key={stepNumber}
            style={isActive ? undefined : { display: "none" }}
          >
            {cloneElement(step, {
              ...extraProps,
              currentStep,
              totalSteps,
              isActive,
              nextStep,
              previousStep,
              goToStep,
              goToNamedStep,
            })}
          </div>
        );
      })}
    </>
  );
};

export default StepWizard;
